import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTooltip } from "context/TooltipProvider";
import {
  addAssetRecoveryReport,
  addReport,
  updateAssetReferral,
} from "services/assetRecoveryService";
import { MultipleFileUploadComponent } from "components/form/MultipleFileUpload";
import { Asset, AssetReferralActionStatusEnum, Referral, Report } from "types";

interface AssetRecoveryFinalReportProps {
  referralId?: string;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
  onNewDataAdded?: (data: any) => void;
  referral: Referral | null;
  assets: Asset[] | null;
  reports?: Report[];
  recoveryReports?: Report[];
  assetsNeedingReport: Asset[];
}

export const AssetRecoveryFinalReport: React.FC<
  AssetRecoveryFinalReportProps
> = ({
  referralId,
  onNewDataAdded,
  setActiveTab,
  referral,
  assets,
  reports = [],
  recoveryReports = [],
  assetsNeedingReport,
}) => {
  const [formData, setFormData] = useState<{
    court_decision_date: string;
    court_decision_summary: string;
    prosecutor_feedback_on_prohabition_decision: string;
    asset: string;
  }>({
    court_decision_date: "",
    court_decision_summary: "",
    prosecutor_feedback_on_prohabition_decision: "",
    asset: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);

  const prohibitedAssets =
    assets?.filter((a) => a.prohabition_court_verdict === "Prohabited") ?? [];

  const finalVerdictReports = (() => {
    if (
      referral?.action_status ===
      AssetReferralActionStatusEnum?.PROHIBITION_ON_LITIGATION
    ) {
      return reports.filter((r) => r.is_next_appointment_reason_final_verdict);
    }
    if (
      referral?.action_status ===
      AssetReferralActionStatusEnum?.ASSET_RECOVERY_ON_LITIGATION
    ) {
      return recoveryReports.filter(
        (r) => r.is_next_appointment_reason_final_verdict,
      );
    }
    return [];
  })();

  const coveredAssetIds = new Set<string>();
  finalVerdictReports.forEach((r) => {
    (r.asset_details ?? []).forEach((a) => {
      if (typeof a === "string") coveredAssetIds.add(a);
      else if (a?.id) coveredAssetIds.add(a.id);
    });
  });

  const coveredCount = prohibitedAssets.filter(
    (a) => a.id && coveredAssetIds.has(a.id),
  ).length;

  const allDone =
    prohibitedAssets.length > 0 && coveredCount === prohibitedAssets.length;

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddAssetRecoveryFinalReport = async () => {
    // ---- basic validation ----
    if (!formData.court_decision_summary.trim()) {
      showTooltip(t("court_decision_summary_is_required"), "error");
      return;
    }
    if (!formData.court_decision_date) {
      showTooltip(t("court_decision_date_is_required"), "error");
      return;
    }
    if (assetsNeedingReport?.length > 0 && !formData.asset) {
      showTooltip(
        t("please_select_asset", { defaultValue: "Please select an asset" }),
        "error",
      );
      return;
    }

    const data = new FormData();
    data.append("referral", referralId as string);
    data.append("court_decision_date", formData.court_decision_date);
    data.append("court_decision_summary", formData.court_decision_summary);
    data.append(
      "prosecutor_feedback_on_prohabition_decision",
      formData.prosecutor_feedback_on_prohabition_decision || "",
    );
    data.append("is_next_appointment_reason_final_verdict", "true");

    // Only append asset if we have one selected (handles case where assetsNeedingReport.length === 0)
    if (formData.asset) {
      data.append("asset", formData.asset);
    }

    uploadedFiles.forEach((f) => data.append("court_decision_documents", f));

    try {
      setSubmitting(true);

      let savedReport;
      if (
        referral?.action_status ===
        AssetReferralActionStatusEnum?.PROHIBITION_ON_LITIGATION
      ) {
        savedReport = await addReport(data);
      } else if (
        referral?.action_status ===
        AssetReferralActionStatusEnum?.ASSET_RECOVERY_ON_LITIGATION
      ) {
        savedReport = await addAssetRecoveryReport(data);
      }

      // FIXED: Check if this is the last report needed
      const isLastReportNeeded = assetsNeedingReport.length <= 1; // If 0 or 1 assets need reports

      // FIXED: Also check if all prohibited assets are now covered
      const totalProhibitedAssets = prohibitedAssets.length;
      const willBeAllCoveredAfterThis =
        coveredCount + (formData.asset ? 1 : 0) >= totalProhibitedAssets ||
        assetsNeedingReport.length === 0;

      if (isLastReportNeeded || willBeAllCoveredAfterThis) {
        const newStatus =
          referral?.action_status ===
          AssetReferralActionStatusEnum?.PROHIBITION_ON_LITIGATION
            ? AssetReferralActionStatusEnum?.PROHIBITION_FINAL_VERDICT
            : AssetReferralActionStatusEnum?.ASSET_RECOVERY_FINAL_VERDICT;

        const response = await updateAssetReferral(referralId as string, {
          action_status: newStatus,
        });

        if (response && onNewDataAdded) {
          onNewDataAdded(response);
          showTooltip(
            t("final_report_submitted_and_case_status_updated", {
              defaultValue: "Final report submitted and case status updated!",
            }),
            "success",
          );
          setActiveTab?.("Recovery Reports");
        }
      } else {
        showTooltip(t("final_report_submitted_successfully"), "success");
      }

      // Reset form
      setFormData({
        court_decision_date: "",
        court_decision_summary: "",
        prosecutor_feedback_on_prohabition_decision: "",
        asset: "",
      });
      setUploadedFiles([]);

      onNewDataAdded?.(savedReport);
    } catch (err) {
      console.error(err);
      showTooltip(t("failed_to_submit_final_report"), "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6">
      <p className="text-sm mb-2 text-gray-400">Fill Final Report</p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleAddAssetRecoveryFinalReport();
        }}
      >
        {assetsNeedingReport.length > 0 ? (
          <div className="flex flex-col mb-4">
            <label className="referral-form-label">Select Asset</label>
            <select
              className="referral-form-field"
              name="asset"
              value={formData.asset}
              onChange={handleInputChange}
            >
              <option value="">Select Asset</option>
              {assetsNeedingReport.map((a) => (
                <option key={a.id} value={a.id!}>
                  {a.asset_unique_id || "Unnamed Asset"}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="mb-4 p-3 bg-green-50 text-green-700 rounded">
            ✅ All assets have final reports.
          </div>
        )}

        {/* ---------- Court Decision Summary ---------- */}
        <div className="flex flex-col mb-4">
          <label className="referral-popup-label">Court Decision Summary</label>
          <textarea
            className="referral-form-field min-h-[100px]"
            name="court_decision_summary"
            placeholder="detail here..."
            value={formData.court_decision_summary}
            onChange={handleInputChange}
          />
        </div>

        {/* ---------- Court Decision Date ---------- */}
        <div className="flex flex-col mb-4">
          <label className="referral-popup-label">Court Decision Date</label>
          <input
            type="date"
            className="referral-form-field"
            name="court_decision_date"
            value={formData.court_decision_date}
            onChange={handleInputChange}
          />
        </div>

        {/* ---------- File upload ---------- */}
        <div className="flex flex-col mb-4">
          <MultipleFileUploadComponent
            onFilesChange={setUploadedFiles}
            uploadedFiles={uploadedFiles}
            label="Report Doc"
          />
        </div>

        {/* ---------- Prosecutor Feedback ---------- */}
        <div className="flex flex-col mb-6">
          <label className="referral-popup-label">Prosecutor Feedback</label>
          <textarea
            className="referral-form-field min-h-[100px]"
            name="prosecutor_feedback_on_prohabition_decision"
            placeholder="Enter prosecutor feedback..."
            value={formData.prosecutor_feedback_on_prohabition_decision}
            onChange={handleInputChange}
          />
        </div>

        {/* ---------- Submit button ---------- */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={
              submitting ||
              assetsNeedingReport.length === 0 ||
              !formData.asset ||
              !formData.court_decision_summary.trim() ||
              !formData.court_decision_date
            }
            className="referral-form-button text-white px-8 py-3"
          >
            {submitting
              ? "Submitting..."
              : assetsNeedingReport.length === 0
                ? "No Assets Left"
                : "Set Court Decision"}
          </button>
        </div>
      </form>
    </div>
  );
};
